// batch-70 vakantiecampagne (FO-525 §B6-§B7) - het jaaroverzicht verlof per potje.
//
// `GET /leave/v2/jaaroverzicht/:employeeId?jaar=` (eigen medewerker of Verlof lezen).
// Live uit het saldo-ledger: start, erbij, opgenomen (toegerekend aan het verlofjaar
// van de aanvraag), nog gepland, correcties, uitbetaald, vervallen en het eind, met de
// reconciliatie tegen de ledgerstand op 31-12. Gedeeld door de wizard van de
// vakantiecampagne, Mijn verlof en de backoffice (`/verlof/saldo`).

import { z } from 'zod'
import { SPAARPOT_LABELS } from './werktijd-sparen'

export const JAAROVERZICHT_EMMERS = ['WETTELIJK', 'BOVENWETTELIJK', 'ATV', 'TVT_WERK', 'TVT_REIS'] as const
export type JaaroverzichtEmmerType = (typeof JAAROVERZICHT_EMMERS)[number]

/** Schermlabels (woordenlijst FO-344: de spaarpotten met hun korte label). */
export const JAAROVERZICHT_EMMER_LABELS: Record<JaaroverzichtEmmerType, string> = {
  WETTELIJK: 'Wettelijk verlof',
  BOVENWETTELIJK: 'Bovenwettelijk verlof',
  ATV: SPAARPOT_LABELS.ATV,
  TVT_WERK: SPAARPOT_LABELS.TVT_WERK,
  TVT_REIS: SPAARPOT_LABELS.TVT_REIS,
}

export const jaaroverzichtQuerySchema = z.object({
  jaar: z.coerce.number().int().min(2020).max(2100).optional(),
})
export type JaaroverzichtQuery = z.infer<typeof jaaroverzichtQuerySchema>

/** Een correctie in het jaar, voor de "i" bij de regel Correcties. */
export interface JaaroverzichtCorrectieRegelDTO {
  mutatieId: string
  /** Wanneer de correctie in het saldo geldt (ISO). */
  datum: string
  /** Wanneer hij werd geboekt (ISO); kan in een later jaar liggen. */
  geboektOp: string | null
  /** Gesigneerd, in uren (twee decimalen; afronden op 0,5 pas bij tonen). */
  uren: number
  bron: string
  bronLabel: string
  toelichting: string | null
  /** Naam van wie hem boekte (null = systeem of onbekend). */
  door: string | null
}

export interface JaaroverzichtEmmerDTO {
  type: JaaroverzichtEmmerType
  label: string
  start: number
  erbij: number
  opgenomen: number
  /** Aangevraagd of goedgekeurd verlof in dit jaar dat nog niet is afgeboekt. */
  nogGereserveerd: number
  correcties: number
  uitbetaald: number
  /** Verval binnen het jaar. Alleen een getal (reconciliatie, backoffice), geen tekst in de wizard. */
  vervallen: number
  eindGeboekt: number
  /** Wat er op 31-12 over is (na het geplande verlof, vóór het verval op de jaargrens). */
  eind: number
  /** Wat er op de jaargrens vervalt; null = niet te bepalen. Niet in de wizard (PO-besluit). */
  vervaltBijJaareinde: number | null
  correctieRegels: JaaroverzichtCorrectieRegelDTO[]
  /** De ledgerstand op 31-12 23:59 (`berekenSaldoOpDatum`). */
  referentie: number
  /** eindGeboekt - referentie; 0 = sluitend. */
  reconciliatieVerschil: number
}

export interface JaaroverzichtTotaalDTO {
  start: number
  erbij: number
  opgenomen: number
  nogGereserveerd: number
  correcties: number
  uitbetaald: number
  vervallen: number
  eindGeboekt: number
  eind: number
}

export interface JaaroverzichtDTO {
  employeeId: string
  jaar: number
  /** Stand per (ISO): het moment van berekenen. */
  standPer: string
  perEmmer: JaaroverzichtEmmerDTO[]
  totaal: JaaroverzichtTotaalDTO
  /** Som van de verschillen per potje; 0 = sluitend. */
  reconciliatieVerschil: number
  /**
   * Sluit het overzicht op het ledger? Zo niet: toon de cijfers met een neutrale melding
   * ("je saldo wordt gecontroleerd") en HR krijgt een signaal. Nooit stil weggewerkt.
   */
  sluitend: boolean
  /** Het jaar is voorbij (NL-tijd): het eind is definitief. */
  jaarAfgesloten: boolean
}
